// ════════════════════════════════════════════════════════════════════════
// lazy-modules.js — fábrica genérica de lazy-loading para módulos de negocio
// pesados hoy importados estáticos en admin.js Y event-bindings.js (cada
// import estático fuerza al bundler a meterlos en el chunk inicial).
//
// Un solo registro de módulos (MODULOS abajo) define TODOS los lazy-loads:
// agregar uno nuevo es una línea acá, no un getter+Proxy a mano por módulo.
// ════════════════════════════════════════════════════════════════════════

// Registro: key = nombre corto usado en getModulo()/proxy(), value = { import, export }.
// import: función () => import('...') — literal para que Vite pueda analizar
// estáticamente el chunk de destino (un import() dinámico con path variable
// no se puede code-splittear). export: nombre del export dentro del módulo.
const MODULOS = {
    platformNodos: { cargar: () => import('../modules/platform-nodos.js'), export: 'PlatformNodos' },
    finanzas: { cargar: () => import('../modules/finanzas.js'), export: 'FinanzasModule' },
    platformDispositivos: {
        cargar: () => import('../modules/platform-dispositivos.js'),
        export: 'PlatformDispositivos',
    },
    clientes: { cargar: () => import('../modules/clientes.js'), export: 'ClientesModule' },
    marketing: { cargar: () => import('../modules/marketing.js'), export: 'MarketingModule' },
    sistema: { cargar: () => import('../modules/sistema.js'), export: 'SistemaModule' },
    platformAdministradores: {
        cargar: () => import('../modules/platform-administradores.js'),
        export: 'PlatformAdministradores',
    },
    tickets: { cargar: () => import('../modules/tickets.js'), export: 'TicketsModule' },
    pozos: { cargar: () => import('../modules/pozos-comunes.js'), export: 'PozosModule' },
    actividad: { cargar: () => import('../modules/actividad.js'), export: 'ActividadModule' },
    marketingCupones: { cargar: () => import('../modules/marketing-cupones.js'), export: 'MarketingCuponesModule' },
    marketingTickets: { cargar: () => import('../modules/marketing-tickets.js'), export: 'MarketingTicketsModule' },
    // export: null → módulo con funciones sueltas exportadas (sin objeto namespace
    // único) — getModulo() devuelve el módulo entero, no una propiedad de él.
    platformSaldosRed: { cargar: () => import('../modules/platform-saldos-red.js'), export: null },
};

const _cache = {};
const _resuelto = {}; // instancia ya resuelta (síncrono), separado de _cache (promesa)

// Resuelve el módulo real (una sola descarga, cacheada). Usar donde ya hay
// `await` disponible (admin.js: dentro de métodos async).
export async function getModulo(key) {
    if (!MODULOS[key]) throw new Error(`[lazy-modules] "${key}" no registrado en MODULOS`);
    if (!_cache[key]) {
        const entrada = MODULOS[key];
        _cache[key] = entrada.cargar().then(m => (_resuelto[key] = entrada.export ? m[entrada.export] : m));
    }
    return _cache[key];
}

// Devuelve la instancia SOLO si ya fue cargada antes (sin disparar un import
// nuevo) — para código que corre en cada navegación (ej. limpiar un timer al
// cambiar de vista) y no debe forzar la descarga de un módulo que el usuario
// nunca visitó. Si nunca se cargó, devuelve null (caller debe no-opear).
export function getModuloSiYaCargado(key) {
    return _resuelto[key] || null;
}

// Como getModulo(), pero además llama .init() en el módulo la PRIMERA vez que
// se resuelve (una sola vez, cacheado) — para módulos cuyo init() antes corría
// en el bootstrap (contenedor DOM ya existe siempre, solo oculto — confirmado
// por auditoría 2026-07-09: sus init() son idempotentes con guard de
// contenedor null, diferir la llamada es equivalente a llamarla en bootstrap).
const _initDone = {};
export async function getModuloConInit(key) {
    const modulo = await getModulo(key);
    if (!_initDone[key]) {
        _initDone[key] = true;
        modulo.init();
    }
    return modulo;
}

// Proxy síncrono para event-bindings.js: sus entradas del diccionario
// (`'nodos:save': () => PlatformNodos.save()`) invocan métodos sin poder
// hacer `await` en el momento del click. El proxy deja CERO líneas de esas
// entradas sin tocar: el primer método invocado dispara el import real en
// background y ejecuta el método apenas resuelve; llamadas subsiguientes
// (ya resuelto) van directo sin pasar de nuevo por la promesa.
const _proxies = {};
export function proxyLazy(key) {
    if (_proxies[key]) return _proxies[key];
    let instancia = null;
    const proxy = new Proxy(
        {},
        {
            get(_target, prop) {
                if (instancia) {
                    const valor = instancia[prop];
                    // bind OBLIGATORIO: sin él, `Proxy.metodo()` corre con `this` = proxy y
                    // los writes de estado (`this._x = ...`) caen en el target vacío mientras
                    // los reads (este trap) leen la instancia → el estado escrito se pierde
                    // (bug real: botón "Calcular prévia" de cascada muerto para siempre).
                    return typeof valor === 'function' ? valor.bind(instancia) : valor;
                }
                return (...args) => getModulo(key).then(m => (instancia = m)[prop](...args));
            },
        }
    );
    _proxies[key] = proxy;
    return proxy;
}

// Wrapper lazy para una función suelta (módulo sin objeto namespace, ej.
// platform-saldos-red.js exporta funciones sueltas, no un objeto con métodos).
// Devuelve una función que resuelve el módulo y llama a `nombreFuncion` en él.
export function proxyLazyFuncion(key, nombreFuncion) {
    return (...args) => getModulo(key).then(m => m[nombreFuncion](...args));
}
